import { Control, FieldValues, Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@/components/nextui'
import { AddressControl, InputControl } from '@/components/form-controls'

interface ConditionalAddressControlProps<T extends FieldValues> {
    name: Path<T>
    control: Control<T>
    rules?: Record<string, any>
    placeholder?: string
    label?: string
    isRequired?: boolean
    className?: string
    countryResidence?: string
    isDisabled?: boolean
    disabledTooltip?: string
    department?: string
}

export function ConditionalAddressControl<T extends FieldValues>({
    name,
    control,
    rules,
    placeholder,
    label,
    isRequired = false,
    className = '',
    countryResidence,
    isDisabled = false,
    disabledTooltip,
    department
}: ConditionalAddressControlProps<T>): React.ReactElement {
    const { t } = useTranslation()

    const renderContent = () => {
        // Si el país de residencia es Colombia, usar el gestor de direcciones
        if (countryResidence === 'CO' || countryResidence === 'Colombia') {
            return (
                <AddressControl
                    name={name}
                    control={control}
                    rules={rules}
                    placeholder={placeholder}
                    label={label}
                    isRequired={isRequired}
                    isDisabled={isDisabled}
                    className={className}
                    department={department}
                />
            )
        }

        // Para otros países, usar un campo de texto simple
        return (
            <InputControl
                name={name}
                control={control}
                label={label}
                placeholder={
                    placeholder ? t(placeholder) : t('address.enterAddress')
                }
                rules={rules}
                isRequired={isRequired}
                isDisabled={isDisabled}
                className={className}
                allowedPattern={/.*/} // Patrón que permite cualquier carácter
                typeInput="text"
            />
        )
    }

    if (isDisabled && disabledTooltip) {
        return (
            <Tooltip content={t(disabledTooltip)} placement="top">
                <div className="w-full">{renderContent()}</div>
            </Tooltip>
        )
    }

    return renderContent()
}
